import { partition } from "lodash";
import { useMemo } from "react";
import { useUser } from "src/contexts/user-context";
import { navigateToWorkspaces } from "src/utils/navigate";
import { switchWorkspace } from "src/utils/workspaces";
import { isPresent } from "ts-extras";
import { useNavWidth } from "src/contexts/nav-width-context";
import { NavSelector, type NavSelectorProps } from "./nav-selector";

type Props = {
  /** Runs the switch for a host with its own way to leave the page, such as saving an open editor first. */
  switchWith?: (go: () => void) => void;
  listWidth?: NavSelectorProps["listWidth"];
  appearance?: NavSelectorProps["appearance"];
  leading?: NavSelectorProps["leading"];
};

const leaveWith = (switchWith: Props["switchWith"], go: () => void) => () =>
  switchWith ? switchWith(go) : go();

export const WorkspaceSelector = ({
  switchWith,
  listWidth,
  appearance,
  leading,
}: Props) => {
  const { workspace, workspaces } = useUser();
  const { isNavOpen } = useNavWidth();

  const menuOptions = useMemo(() => {
    const [activeWorkspaces, inactiveWorkspaces] = partition(
      workspaces,
      ({ slug }) => slug === workspace?.slug,
    );
    const activeWorkspace = activeWorkspaces?.[0];
    const orderedWorkspaces = [activeWorkspace]
      .concat(inactiveWorkspaces)
      .filter(isPresent);
    const options = orderedWorkspaces?.map(({ name, id, slug }) => ({
      active: slug === workspace?.slug,
      label: name,
      onClick: leaveWith(
        switchWith,
        () => void switchWorkspace(id, `/${slug}`),
      ),
    }));
    return options ?? [];
  }, [workspaces, workspace?.slug, switchWith]);

  return (
    <NavSelector
      ariaLabel="Select workspace"
      displayLabel={workspace?.name ?? "Select workspace"}
      options={menuOptions}
      isNavOpen={isNavOpen}
      // The header's row is a nav row's height; the field keeps its own.
      height={appearance === "header" ? 8 : 10}
      topAction={{
        label: "View all workspaces",
        onClick: leaveWith(switchWith, navigateToWorkspaces),
      }}
      showSearchbar={true}
      searchPlaceholder="Search workspaces"
      listWidth={listWidth}
      appearance={appearance}
      leading={leading}
    />
  );
};
